import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { serversApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import type { EdgeConfigResult, EdgeDeviceChannel } from '../../types'
import { flattenEdgeChannelInfo, toEdgeChannelInfo } from '../deviceSpec/channelShape'
import { inputClass, useSubmit } from './dialogShared'
import { Dialog, Footer, Label } from './MasterGroupDialogs'

// 디바이스 추가/수정 — 저장은 엣지 Parameter.xml 로 바로 간다 (EM 은 저장하지 않음).
// 채널 설정은 유형마다 필드가 달라 엣지가 주는 channelInfo(값 + "<필드>Pairs" 선택지)만 보고 폼을 그린다.

type ChannelInfo = Record<string, unknown>

// 자주 나오는 필드 이름 — 없으면 필드 이름 그대로 보여준다
const FIELD_LABELS: Record<string, string> = {
  interval: '수집 주기 (ms)',
  ip: 'IP',
  port: '포트',
  socketType: '소켓',
  cpuType: 'CPU 타입',
  maxMemorySize: '최대 메모리 크기',
  serverUri: '서버 URI',
  account: '계정',
  password: '비밀번호',
  authenticationType: '인증 방식',
  securityModeType: '보안 모드',
  securityPolicyType: '보안 정책',
  messageEncodingType: '메시지 인코딩',
  comPort: 'COM 포트',
  baudRate: 'Baud rate',
  dataBits: 'Data bits',
  stopBits: 'Stop bits',
  parity: 'Parity',
  station: '국번',
  networkNo: '네트워크 번호',
  pcStationNo: 'PC 국번',
  plcStationNo: 'PLC 국번',
  frameType: '프레임',
  packetDataType: '데이터 형식',
  packetType: '패킷 형식',
  cpuNo: 'CPU 번호',
  path: '경로',
  folderKey: '폴더 키',
}

const isPassword = (key: string) => key.toLowerCase() === 'password'

// 편집할 필드 — 선택지 배열("…Pairs")과 중첩 객체는 뺀다
function editableFields(info: ChannelInfo) {
  return Object.keys(info).filter(k => !k.endsWith('Pairs') && (info[k] == null || typeof info[k] !== 'object'))
}

function optionsOf(info: ChannelInfo, key: string): string[] | null {
  const pairs = info[`${key}Pairs`]
  return Array.isArray(pairs) && pairs.length > 0 ? pairs.map(String) : null
}

function parseInfo(json: string): ChannelInfo {
  try {
    const parsed: unknown = JSON.parse(json)
    // Custom 은 value 안에 접속 칸이 중첩돼 온다 — 폼에서는 펴서 보이고 저장 때 다시 중첩한다
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? flattenEdgeChannelInfo(parsed as ChannelInfo) : {}
  } catch {
    return {}
  }
}

// device 가 있으면 수정 (코드·채널 유형 고정), 없으면 유형을 골라 추가
export default function DeviceDialog({ serverId, deviceCode, onClose, onDone }: {
  serverId: string
  deviceCode?: string
  onClose: () => void
  onDone: (result: EdgeConfigResult) => void
}) {
  const isNew = deviceCode == null
  const [channelType, setChannelType] = useState('')

  const typesQuery = useQuery({
    queryKey: ['servers', serverId, 'channel-types'],
    queryFn: () => serversApi.getChannelTypes(serverId).then(r => r.data),
    enabled: isNew,
    retry: false,
    staleTime: Infinity,
  })

  // 수정: 현재 설정, 추가: 고른 유형의 템플릿 (둘 다 엣지에 즉시 요청)
  const sourceQuery = useQuery({
    queryKey: isNew
      ? ['servers', serverId, 'device-template', channelType]
      : ['servers', serverId, 'device-channel', deviceCode],
    queryFn: () => (isNew
      ? serversApi.getDeviceTemplate(serverId, channelType)
      : serversApi.getDeviceChannel(serverId, deviceCode)).then(r => r.data),
    enabled: isNew ? !!channelType : true,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  const title = isNew ? '디바이스 추가' : `디바이스 설정 — ${deviceCode}`
  const loadError = typesQuery.error ?? sourceQuery.error

  return (
    <Dialog title={title} wide onClose={onClose}
      footer={sourceQuery.data ? null : (
        <div className="flex justify-end mt-5">
          <button onClick={onClose}
            className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition">닫기</button>
        </div>
      )}>
      {isNew && (
        <Label text="채널 유형" required>
          <select value={channelType} onChange={e => setChannelType(e.target.value)} className={inputClass} autoFocus
            disabled={typesQuery.isLoading}>
            <option value="">{typesQuery.isLoading ? '불러오는 중…' : '선택하세요'}</option>
            {(typesQuery.data ?? []).map(t => <option key={t.name} value={t.name}>{t.description}</option>)}
          </select>
        </Label>
      )}
      {loadError && <p className="text-xs text-red-600">{getErrorMessage(loadError, '설정을 받지 못했습니다.')}</p>}
      {sourceQuery.isFetching && <p className="text-xs text-gray-400">Edge에서 설정을 받는 중…</p>}
      {sourceQuery.data && !sourceQuery.isFetching && (
        // 유형을 바꾸면 폼을 새로 만든다 (key)
        <DeviceForm key={sourceQuery.data.channelType} serverId={serverId} source={sourceQuery.data} isNew={isNew}
          onClose={onClose} onDone={onDone} />
      )}
    </Dialog>
  )
}

function DeviceForm({ serverId, source, isNew, onClose, onDone }: {
  serverId: string
  source: EdgeDeviceChannel
  isNew: boolean
  onClose: () => void
  onDone: (result: EdgeConfigResult) => void
}) {
  const [code, setCode] = useState(source.code)
  const [name, setName] = useState(source.name)
  const [description, setDescription] = useState(source.description ?? '')
  const [info, setInfo] = useState<ChannelInfo>(() => parseInfo(source.channelInfoJson))
  const { saving, error, submit } = useSubmit(onDone)

  const fields = editableFields(info)
  const setField = (key: string, value: string) => setInfo(prev => ({ ...prev, [key]: value }))

  const save = () => {
    const body = {
      channelType: source.channelType,
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || null,
      channelInfoJson: JSON.stringify(toEdgeChannelInfo(source.channelType, info)),
    }
    submit(() => (isNew ? serversApi.createDevice(serverId, body) : serversApi.updateDevice(serverId, body)))
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Label text="디바이스 코드" required>
          <input value={code} onChange={e => setCode(e.target.value)} disabled={!isNew} className={`${inputClass} font-mono`} />
        </Label>
        <Label text="이름" required>
          <input value={name} onChange={e => setName(e.target.value)} className={inputClass} />
        </Label>
      </div>
      {!isNew && <p className="text-xs text-gray-400 -mt-1">디바이스 코드는 태그 코드·수집 로그의 키라 바꿀 수 없습니다.</p>}
      <Label text="설명">
        <input value={description} onChange={e => setDescription(e.target.value)} className={inputClass} />
      </Label>

      <div className="pt-2 border-t border-gray-100">
        <div className="text-xs font-medium text-gray-500 mb-2">채널 설정 · {source.channelType}</div>
        {fields.length === 0 ? (
          <p className="text-xs text-gray-400">설정할 항목이 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {fields.map(key => {
              const options = optionsOf(info, key)
              const value = info[key] == null ? '' : String(info[key])
              return (
                <Label key={key} text={FIELD_LABELS[key] ?? key}>
                  {options ? (
                    <select value={value} onChange={e => setField(key, e.target.value)} className={inputClass}>
                      {!options.includes(value) && <option value={value}>{value || '-'}</option>}
                      {options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input value={value} onChange={e => setField(key, e.target.value)}
                      type={isPassword(key) ? 'password' : 'text'}
                      placeholder={isPassword(key) && !isNew ? '비워 두면 기존 값 유지' : undefined}
                      autoComplete={isPassword(key) ? 'new-password' : 'off'}
                      className={`${inputClass} font-mono`} />
                  )}
                </Label>
              )
            })}
          </div>
        )}
      </div>

      <Footer saving={saving} error={error} disabled={!code.trim() || !name.trim()} onClose={onClose} onSubmit={save} />
    </>
  )
}
